/**
 * LinesTable — Sous-composant extrait de SalesDocumentEditor.
 *
 * Rendu de la table des lignes (Désignation, Qté, PU HT, TVA, Remise, Total HT).
 * Reste 100% contrôlé par le parent : reçoit `lines`, `readOnly`, et les
 * callbacks `onUpdate(index, key, value)`, `onAdd()`, `onRemove(index)`.
 *
 * Isolé pour :
 *   1. Alléger SalesDocumentEditor.jsx (>720 lignes → ~650)
 *   2. Rendre la table testable indépendamment
 *   3. Faciliter la future extraction identique dans PurchasesEditor
 */
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import ProductAutocomplete from "@/components/ProductAutocomplete";

const VAT_RATES = [20, 10, 5.5, 2.1, 0];

const fmtEuro = (n) =>
  `${(Number(n) || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} €`;

export default function LinesTable({ lines, readOnly, onUpdate, onAdd, onRemove, onSelectProduct }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.05 }}>
      <Card className="border-slate-200 bg-white rounded-xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>Lignes</h3>
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={onAdd}
                    className="text-blue-700 hover:bg-blue-50" data-testid="add-line-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
            </Button>
          )}
        </div>
        <Table data-testid="lines-table">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">Désignation</TableHead>
              <TableHead className="min-w-[110px] text-right" title="Quantité">Quantité</TableHead>
              <TableHead className="min-w-[130px] text-right" title="Prix unitaire hors taxes">
                Prix unitaire HT
              </TableHead>
              <TableHead className="min-w-[90px]" title="Taux de TVA appliqué à la ligne">
                Taux TVA
              </TableHead>
              <TableHead className="min-w-[100px] text-right" title="Remise en pourcentage sur cette ligne uniquement">
                Remise %
              </TableHead>
              <TableHead className="min-w-[130px] text-right">Total ligne HT</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((ln, i) => {
              const disc = Number(ln.discount_percent ?? ln.discount_pct ?? 0);
              const net = +(Number(ln.quantity || 0) * Number(ln.unit_price_ht || 0) *
                              (1 - disc / 100)).toFixed(2);
              return (
                <TableRow key={i} data-testid={`line-row-${i}`}>
                  <TableCell>
                    {readOnly ? (
                      <Input id={`line-desc-${i}`} name={`line-desc-${i}`} value={ln.description || ""} disabled placeholder="Prestation / produit"
                              data-testid={`line-desc-${i}`} />
                    ) : (
                      <ProductAutocomplete
                        id={`line-desc-${i}`}
                        name={`line-desc-${i}`}
                        value={ln.description || ""}
                        onChange={(v) => onUpdate(i, "description", v)}
                        onSelectProduct={(p) => onSelectProduct?.(i, p)}
                        placeholder="Tapez pour piocher dans le catalogue…"
                        data-testid={`line-desc-${i}`}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Input id={`line-qty-${i}`} name={`line-qty-${i}`} type="number" min="0" step="1" value={ln.quantity} disabled={readOnly}
                            onChange={(e) => onUpdate(i, "quantity", e.target.value)}
                            className="text-right font-mono w-full" data-testid={`line-qty-${i}`} />
                  </TableCell>
                  <TableCell>
                    <Input id={`line-price-${i}`} name={`line-price-${i}`} type="number" min="0" step="0.0001" value={ln.unit_price_ht} disabled={readOnly}
                            onChange={(e) => onUpdate(i, "unit_price_ht", e.target.value)}
                            className="text-right font-mono w-full" data-testid={`line-price-${i}`} />
                  </TableCell>
                  <TableCell>
                    <Select value={String(ln.vat_rate)} disabled={readOnly}
                             onValueChange={(v) => onUpdate(i, "vat_rate", Number(v))}>
                      <SelectTrigger className="h-9 font-mono" data-testid={`line-vat-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map((r) => (
                          <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input id={`line-discount-${i}`} name={`line-discount-${i}`} type="number" min="0" max="100" step="0.1"
                            value={ln.discount_percent ?? 0} disabled={readOnly}
                            onChange={(e) => onUpdate(i, "discount_percent", e.target.value)}
                            className="text-right font-mono" data-testid={`line-discount-${i}`} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-800 font-mono">
                    {fmtEuro(net)}
                  </TableCell>
                  <TableCell>
                    {lines.length > 1 && !readOnly && (
                      <Button variant="ghost" size="icon" onClick={() => onRemove(i)}
                               data-testid={`line-remove-${i}`}
                               className="h-8 w-8 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {lines.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            Aucune ligne. Cliquez sur « Ajouter ».
          </div>
        )}
      </Card>
    </motion.div>
  );
}
